import React from 'react';
import { Button, Form, Input, Space } from 'antd';
import type { FormInstance } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { ReactComponent as Trash } from 'assets/icons/trash.svg';
import { EIQFormLabel } from 'common/components/label/form-label';
import featureStore from 'common/stores/features/feature.store';
import { observer } from 'mobx-react-lite';
import CitationRuleEscalationTierConfiguration from './citation-rule-escalation-tier-configuration';
import { ICitationConfigurationFormValues } from './citation-configuration-form.types';
import { createEmptyEscalationTier } from './citation-configuration.helper';
import './citations-configurations.scss';

interface CitationRuleConfigurationProps {
  index: number;
  name: number;
  restField: Record<string, unknown>;
  form: FormInstance<ICitationConfigurationFormValues>;
  remove: (index: number) => void;
}

const CitationRuleConfiguration = observer(({ index, name, restField, remove, form }: CitationRuleConfigurationProps) => {
  const isEscalationTiersEnabled = featureStore.isEIQIntelliTicketEscalationTiersEnabled;

  return (
    <Space className="citation-rule-wrapper" direction="vertical">
      <div className="rule-index">Citation {index + 1}</div>

      <div className="citation-escalation-tiers">
        {isEscalationTiersEnabled && <div className="citation-levels-title">Escalation Tiers</div>}

        <div className="citation-escalation-tiers-list">
          <Form.List name={[name, 'escalationTiers']}>
            {(tierFields, { add, remove: removeTier }) => {
              const visibleTierFields = isEscalationTiersEnabled ? tierFields : tierFields.slice(0, 1);

              const revalidateTiers = () => {
                void form.validateFields([['rules', name, 'escalationTiers']]);
              };

              return (
                <>
                  {visibleTierFields.map(({ key: tierKey, name: tierName, ...tierRestField }, tierIndex) => (
                    <CitationRuleEscalationTierConfiguration
                      key={tierKey}
                      index={tierIndex}
                      ruleName={name}
                      name={tierName}
                      restField={tierRestField}
                      form={form}
                      remove={(tierFieldName) => {
                        removeTier(tierFieldName);
                        revalidateTiers();
                      }}
                      canRemove={isEscalationTiersEnabled && tierFields.length > 1}
                      isLast={!isEscalationTiersEnabled || tierIndex === visibleTierFields.length - 1}
                    />
                  ))}

                  {isEscalationTiersEnabled && (
                    <Form.Item>
                      <Button
                        type="dashed"
                        block
                        icon={<PlusOutlined />}
                        onClick={() => {
                          add(createEmptyEscalationTier(tierFields.length));
                          revalidateTiers();
                        }}>
                        Add escalation tier
                      </Button>
                    </Form.Item>
                  )}
                </>
              );
            }}
          </Form.List>
        </div>
      </div>

      <Form.Item
        {...restField}
        label={
          <EIQFormLabel
            title="Citation Information"
            description="These notes appear at the top of the email notification and on the PDF citation. Email addresses in the text will be automatically converted into clickable links."
          />
        }
        name={[name, 'legalNotes']}
        rules={[{ max: 1000, message: 'Maximum length is 1000 characters' }]}>
        <Input.TextArea rows={3} maxLength={1000} />
      </Form.Item>

      {index > 2 && (
        <Button type="text" icon={<Trash />} onClick={() => remove(name)}>
          Remove rule
        </Button>
      )}
    </Space>
  );
});

export default CitationRuleConfiguration;
